import { z } from 'zod'
import { BaseLayerTypeEnumValues, MarketMakerRequestStatusEnumValues, SectorTypeEnumValues } from '@forgd/shared/enums'

export * from './request'
import { FORGD_SYSTEM_COINGECKO_ID } from '@forgd/shared/constants'
import { GetMeProject } from '../auth'

export const BaseAdvisorEngagementBody = z.object({
  advisorOrganizationId: z.string().uuid(),
  advisorEditorIds: z.array(z.string().uuid()).min(1),
  isEngagedAsAdvisor: z.boolean(),
})

const advisorEngagementFields = {
  image: z
    .string()
    .nullable()
    .refine((val) => val !== null && val.trim() !== '', {
      message: 'Token Profile Image is required',
    }),
  name: z.string().trim().min(1, { message: 'Token name is required.' }),
  ticker: z
    .string()
    .trim()
    .min(1, { message: 'Token ticker is required.' })
    .transform((val) => val.toUpperCase())
    .refine((val) => val !== FORGD_SYSTEM_COINGECKO_ID, {
      message: 'Token ticker cannot be system token',
    }),
  tgeDate: z.string().min(1, { message: 'TGE Date is required.' }).date(),
  link: z.string().url({ message: 'Token website should be a valid URL.' }),
  sector: z.array(z.enum(SectorTypeEnumValues)).nonempty({ message: 'Project Sector is required.' }),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).nonempty({ message: 'Base Layer is required.' }),
}

const PostTgeAdvisorEngagementBody = BaseAdvisorEngagementBody.extend({
  tokenListed: z.literal(true),
  coingeckoId: z.string().refine((val) => (val && val.trim() === FORGD_SYSTEM_COINGECKO_ID ? false : true), {
    message: 'Coingecko cannot be system token',
  }),
  ...advisorEngagementFields,
})

const PreTgeAdvisorEngagementBody = BaseAdvisorEngagementBody.extend({
  tokenListed: z.literal(false),
  ...advisorEngagementFields,
})

export const AdvisorEngagementBody = z.discriminatedUnion('tokenListed', [
  PostTgeAdvisorEngagementBody,
  PreTgeAdvisorEngagementBody,
])

const BaseAdvisorEngagementProject = GetMeProject.extend({
  ownerUserId: z.string().uuid(),
  rfqStatus: z.enum(MarketMakerRequestStatusEnumValues).nullable(),
  rfqRound: z.number().nullable(),
  rfqQuotesSubmittedQty: z.number().nullable(),
  rfqQuotesAcceptedQty: z.number().nullable(),
  hasRfq: z.boolean(),
  advisorEditors: z.array(
    z.object({
      id: z.string().uuid(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().email(),
    }),
  ),
})

const PreTgeProject = BaseAdvisorEngagementProject.extend({
  tokenListed: z.literal(false),
  isTdPublished: z.boolean(),
})

const PostTgeProject = BaseAdvisorEngagementProject.extend({
  tokenListed: z.literal(true),
  tokenPrice: z.number().nullable(),
  priceChangePercentage24h: z.number().nullable(),
  monitoredMarketMakers: z.array(
    z.object({
      image: z.string().nullable(),
      name: z.string(),
    }),
  ),
  monitoredMarketMakersCount: z.number(),
})

export const AdvisorEngagementProject = z.discriminatedUnion('tokenListed', [PreTgeProject, PostTgeProject])

export const AdvisorEngagementProjectsResponse = z.object({
  projects: z.array(AdvisorEngagementProject),
  preTgeCount: z.number(),
  postTgeCount: z.number(),
})

export const AdvisorEngagementProjectResponse = BaseAdvisorEngagementProject.extend({
  tokenListed: z.boolean(),
  coingeckoId: z.string().nullable(),
  tgeDate: z.string().date().nullable(),
  isTdPublished: z.boolean(),
  isEngagedAsAdvisor: z.boolean(),
  link: z.string().url().nullable(),
  sector: z.array(z.enum(SectorTypeEnumValues)),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)),
  monitoredMarketMakers: z.array(
    z.object({
      image: z.string().nullable(),
      name: z.string(),
    }),
  ),
})

export type PostTgeProjectType = z.infer<typeof PostTgeProject>
export type PreTgeProjectType = z.infer<typeof PreTgeProject>
export type AdvisorEngagementProjectsResponseType = z.infer<typeof AdvisorEngagementProjectsResponse>
export type AdvisorEngagementBodyType = z.infer<typeof AdvisorEngagementBody>
export type BaseAdvisorEngagementBodyType = z.infer<typeof BaseAdvisorEngagementBody>
